import { ErrorInfo, useCallback, useMemo } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { type Product } from '../../product-subnav';
import { type CCPKey } from '../common/constants';

export type RecommendationsViewedProps = {
	recommendations: string[];
	isMobile: boolean;
};

export function useAnalytics() {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const commonProps = useMemo(
		() => ({
			product: 'marketing',
		}),
		[],
	);

	const fireCardClicked = useCallback(
		({
			container,
			product,
			isMobile,
		}: {
			container: 'ProductsRecommendationsContainer' | 'NewProductsContainer';
			product: Product;
			isMobile: boolean;
		}) => {
			createAnalyticsEvent({
				...commonProps,
				eventType: 'ui',
				action: 'clicked',
				actionSubject: 'linkButton',
				actionSubjectId: 'recommendedProduct',
				attributes: {
					productKey: product.id,
					eventContainer: container,
					isMobile,
				},
			}).fire();
		},
		[commonProps, createAnalyticsEvent],
	);

	const fireGlobalNavProductRecommendationsViewed = useCallback(
		({ recommendations, isMobile }: RecommendationsViewedProps) => {
			createAnalyticsEvent({
				...commonProps,
				eventType: 'screen',
				name: 'productNavRecommendation',
				attributes: {
					recommendedProductKeys: recommendations,
					isMobile,
				},
			}).fire();
		},
		[commonProps, createAnalyticsEvent],
	);

	const fireFetchGrowthRecommendationSucceeded = useCallback(
		(recommendedProductIds: CCPKey[], elapsedTimeMiliseconds: number) => {
			createAnalyticsEvent({
				...commonProps,
				eventType: 'operational',
				actionSubject: 'fetchGrowthRecommendation',
				action: 'succeeded',
				attributes: {
					recommendedProductIds,
					elapsedTime: elapsedTimeMiliseconds / 1000,
				},
			}).fire();
		},
		[commonProps, createAnalyticsEvent],
	);

	const fireFetchGrowthRecommendationFailed = useCallback(
		(error: string, elapsedTimeMiliseconds: number) => {
			createAnalyticsEvent({
				...commonProps,
				eventType: 'operational',
				actionSubject: 'fetchGrowthRecommendation',
				action: 'failed',
				attributes: {
					errorMessage: error,
					elapsedTime: elapsedTimeMiliseconds / 1000,
				},
			}).fire();
		},
		[commonProps, createAnalyticsEvent],
	);

	const fireRenderGlobalNavProductRecommendationsFailed = useCallback(
		(error: Error, errorInfo: ErrorInfo) => {
			createAnalyticsEvent({
				...commonProps,
				eventType: 'operational',
				actionSubject: 'renderGlobalNavProductRecommendations',
				action: 'failed',
				attributes: {
					errorMessage: error.message,
					stackTrace: errorInfo.componentStack,
				},
			}).fire();
		},
		[commonProps, createAnalyticsEvent],
	);

	return {
		fireCardClicked,
		fireGlobalNavProductRecommendationsViewed,
		fireFetchGrowthRecommendationSucceeded,
		fireFetchGrowthRecommendationFailed,
		fireRenderGlobalNavProductRecommendationsFailed,
	};
}
